// SPDX-License-Identifier: AGPL-3.0-or-later

import {ImagePreviewField} from '@app/features/app/components/shared/ImagePreviewField';
import {EXAMPLE_BOT_NAME, FLUXER_TAG_LABEL} from '@app/features/app/config/I18nDisplayConstants';
import type {DeveloperApplication} from '@app/features/devtools/models/DeveloperApplication';
import {Input, Textarea} from '@app/features/ui/components/form/Input';
import {Switch} from '@app/features/ui/components/form/Switch';
import {UsernameValidationRules} from '@app/features/ui/components/form/UsernameValidationRules';
import styles from '@app/features/user/components/modals/tabs/applications_tab/application_detail/ApplicationDetail.module.css';
import type {ApplicationDetailForm} from '@app/features/user/components/modals/tabs/applications_tab/application_detail/ApplicationDetailTypes';
import {SectionCard} from '@app/features/user/components/modals/tabs/applications_tab/application_detail/SectionCard';
import {AvatarUploader} from '@app/features/user/components/modals/tabs/my_profile_tab/AvatarUploader';
import {BannerUploader} from '@app/features/user/components/modals/tabs/my_profile_tab/BannerUploader';
import {useLingui} from '@lingui/react/macro';
import type React from 'react';
import {Controller} from 'react-hook-form';

interface BotProfileSectionProps {
	application: DeveloperApplication;
	form: ApplicationDetailForm;
	displayAvatarUrl: string | null;
	hasAvatar: boolean;
	hasClearedAvatar: boolean;
	displayBannerUrl: string | null;
	hasBanner: boolean;
	hasClearedBanner: boolean;
	onAvatarChange: (value: string) => void;
	onAvatarClear: () => void;
	onBannerChange: (value: string) => void;
	onBannerClear: () => void;
}
export const BotProfileSection: React.FC<BotProfileSectionProps> = ({
	application,
	form,
	displayAvatarUrl,
	hasAvatar,
	hasClearedAvatar,
	displayBannerUrl,
	hasBanner,
	hasClearedBanner,
	onAvatarChange,
	onAvatarClear,
	onBannerChange,
	onBannerClear,
}) => {
	const {t} = useLingui();
	const friendlyFlagEnabled = form.watch('friendlyBot') ?? false;
	return (
		<SectionCard title={t`Bot Profile`} subtitle={t`Avatar, tag, and rich profile details for your bot.`}>
			<div className={styles.fieldStack}>
				<div className={styles.avatarRow}>
					{displayAvatarUrl ? (
						<img src={displayAvatarUrl} alt={t`Bot avatar`} className={styles.avatarPreview} />
					) : (
						<div className={styles.avatarPlaceholder}>{application.bot?.username.charAt(0).toUpperCase()}</div>
					)}
					<AvatarUploader
						hasAvatar={hasAvatar && !hasClearedAvatar}
						onAvatarChange={onAvatarChange}
						onAvatarClear={onAvatarClear}
						isPerGuildProfile={false}
						errorMessage={form.formState.errors.avatar?.message}
					/>
				</div>
				<div className={styles.tagRow}>
					<Controller
						name="username"
						control={form.control}
						rules={{
							required: t`Username is required`,
							minLength: {value: 1, message: t`Username must be at least 1 character`},
							maxLength: {value: 32, message: t`Username must be at most 32 characters`},
							pattern: {
								value: /^[a-zA-Z0-9_]+$/,
								message: t`Username can only contain letters, numbers, and underscores`,
							},
						}}
						render={({field}) => (
							<Input
								{...field}
								aria-label={t`Bot Username`}
								placeholder={EXAMPLE_BOT_NAME}
								maxLength={32}
								required
								label={FLUXER_TAG_LABEL}
							/>
						)}
					/>
					<div className={styles.discriminatorInput}>
						<Input
							value={application.bot?.discriminator}
							readOnly
							disabled
							maxLength={4}
							aria-label={t`Discriminator`}
						/>
					</div>
				</div>
				{form.formState.errors.username && (
					<div className={styles.error}>{form.formState.errors.username?.message}</div>
				)}
				<div className={styles.validationBox}>
					<UsernameValidationRules username={form.watch('username') || ''} />
				</div>
				<Controller
					name="bio"
					control={form.control}
					render={({field}) => (
						<Textarea
							ref={field.ref}
							name={field.name}
							onBlur={field.onBlur}
							label={t`Bot Bio`}
							value={field.value ?? ''}
							onChange={(event) => field.onChange(event.target.value)}
							placeholder={t`A helpful bot that does amazing things!`}
							minRows={3}
							maxRows={6}
							maxLength={1024}
							error={form.formState.errors.bio?.message}
						/>
					)}
				/>
				<div className={styles.bannerRow}>
					<BannerUploader
						hasBanner={hasBanner}
						onBannerChange={onBannerChange}
						onBannerClear={onBannerClear}
						isPerGuildProfile={false}
						errorMessage={form.formState.errors.banner?.message as string | undefined}
					/>
				</div>
				<ImagePreviewField
					imageUrl={hasBanner && !hasClearedBanner ? displayBannerUrl : null}
					showPlaceholder={!hasBanner || hasClearedBanner}
					placeholderText={t`No bot banner`}
					altText={t`Bot banner preview`}
					objectFit="contain"
				/>
				<Controller
					name="friendlyBot"
					control={form.control}
					render={({field}) => (
						<Switch
							label={t`Friendly bot`}
							description={t`Allow users to send this bot friend requests for manual approval.`}
							value={Boolean(field.value)}
							onChange={field.onChange}
						/>
					)}
				/>
				<Controller
					name="botManualFriendRequestApproval"
					control={form.control}
					render={({field}) => (
						<Switch
							label={t`Require manual friend approval`}
							description={t`When enabled, you must accept friend requests to this bot manually.`}
							value={Boolean(field.value)}
							onChange={field.onChange}
							disabled={!friendlyFlagEnabled}
						/>
					)}
				/>
			</div>
		</SectionCard>
	);
};
